import React, { useEffect, useContext, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

import {
  _t,
  getCookie,
  tableLoading,
  formatPrice,
  customStyle,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useCustomHooks } from "../../../../../functions/Hooks";

import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ManageSidebar from "../ManageSidebar";

import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import { useGeneralHooks } from "../../../../../hooks/generalHooks";

const LIST_PATH = "/dashboard/manage/stock/opening-stock";

const commonToast = {
  position: "bottom-center",
  autoClose: 10000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  className: "text-center toast-notification",
};

const OpeningStockAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { enableStockBrand } = useGeneralHooks()
  const isEditMode = Boolean(id);
  const pageTitle = isEditMode ? "Edit Opening Stock" : "Add Opening Stock";

  const { roundOffStock, qtyAmountPoints } = useCustomHooks();
  const rowId = useRef(0);

  const {
    loading,
    setLoading,
    activeBrand,
    setBrandPagination,
    branchForSearch,
    ingredientItemForSearch,
  } = useContext(RestaurantContext);

  const { setOpeningStockList, setOpeningStockForSearch } = useContext(UserContext);

  const [newOpeningStock, setNewOpeningStock] = useState({
    document_no: "",
    branch: null,
    remarks: "",
    item_amount: 0,
  });
  const [newRow, setNewRow] = useState([]);
  const [editStock, setEditStock] = useState(null);
  const [documentDate, setDocumentDate] = useState(new Date());

  const getOpeningStockItem = async () => {
    setLoading(true);
    const url = `${BASE_URL}/settings/get-opening-stock-edit/${parseInt(id, 10)}`;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res) {
        setEditStock(res.data);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeBrand) {
      setBrandPagination(1);
    }
    if (isEditMode && id) {
      getOpeningStockItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  useEffect(() => {
    if (editStock && branchForSearch) {
      const docDate = new Date(editStock.stock.document_date);
      const branch = branchForSearch.find(
        (item) => editStock.stock.branch_id === item.id
      );

      setNewOpeningStock({
        document_no: editStock.stock.document_identity,
        branch,
        remarks: editStock.stock.remarks,
        item_amount: 0,
      });
      setDocumentDate(docDate);

      const rowArr = [];
      if (editStock.stockDetails?.length > 0) {
        editStock.stockDetails.forEach((item) => {
          rowArr.push({
            id: rowId.current,
            item_id: item.item_id,
            item_name: item.item_name,
            brand: item.brand || "",
            qty: roundOffStock(item.qty),
            unit_id: item.unit_id,
            unit: item.unit_name,
            rate: roundOffStock(item.rate),
            amount: roundOffStock(item.amount),
            base_unit: item.base_unit,
            base_qty: roundOffStock(item.base_qty),
            conversion_qty: roundOffStock(item.conversion_qty),
            isEdit: true,
          });
          rowId.current += 1;
        });
        setNewRow(rowArr);
      }
    }
  }, [editStock, branchForSearch, roundOffStock]);

  const getUnits = async (itemId, index) => {
    const url = `${BASE_URL}/settings/get-unit_item/${itemId}`;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setNewRow((prev) => {
          const updatedRows = [...prev];
          updatedRows[index].item_units = res.data;
          return updatedRows;
        });
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  const handleSetItems = async (index, item) => {
    const updatedRows = [...newRow];
    updatedRows[index].unit = "";
    updatedRows[index].base_unit = "";
    updatedRows[index].base_qty = "";
    updatedRows[index].conversion_qty = "";
    updatedRows[index].item_name = item;
    updatedRows[index].base_unit = item.unit_id;
    updatedRows[index].item_units = "";
    setNewRow(updatedRows);
    await getUnits(item.id, index);
  };

  const handleSetUnit = (index, unit) => {
    const updatedRows = [...newRow];
    updatedRows[index].unit = unit;
    updatedRows[index].conversion_qty = roundOffStock(unit.qty).toFixed(2);
    setNewRow(updatedRows);
  };

  const calBaseQty = (index) => {
    const qty = newRow[index].qty;
    const conversionQty = newRow[index].conversion_qty;
    if (qty && conversionQty) {
      return roundOffStock(qty * conversionQty);
    }
    return "";
  };

  const addNewRow = () => {
    if (!isEditMode && newOpeningStock.branch === null) {
      toast.error(`${_t(t("Please select branch"))}`, commonToast);
      return;
    }

    const newID = rowId.current;
    setNewRow((prev) => [
      ...prev,
      {
        id: newID,
        item_name: "",
        brand: "",
        qty: "",
        unit: "",
        item_units: "",
        rate: "",
        amount: "",
        total: "",
        base_unit: "",
        base_qty: "",
        conversion_qty: "",
        isEdit: false,
      },
    ]);
    rowId.current += 1;
  };

  const delRow = (rowItemId) => {
    setNewRow(newRow.filter((item) => rowItemId !== item.id));
  };

  const handleAmount = () => {
    let total = 0;
    if (newRow?.length > 0) {
      newRow.forEach((item) => {
        if (parseFloat(item.qty) && parseFloat(item.rate)) {
          total += parseFloat(item.qty) * parseFloat(item.rate);
        }
      });
    }
    return roundOffStock(total);
  };

  const handleGetAmount = (index) => {
    let qty = 0;
    let rate = 0;
    if (newRow?.[index]?.qty !== "") {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow?.[index]?.rate !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return roundOffStock(rate * qty);
  };

  const handleItemQty = (index, e) => {
    const updatedRows = [...newRow];
    updatedRows[index].qty = e.target.value;
    setNewRow(updatedRows);
  };

  const handleItemRate = (index, e) => {
    const updatedRows = [...newRow];
    updatedRows[index].rate = e.target.value;
    setNewRow(updatedRows);
  };

  const handleSetBrand = (index, e) => {
    const updatedRows = [...newRow];
    updatedRows[index].brand = e;
    setNewRow(updatedRows);
  };

  const handleSetBranch = (branch) => {
    setNewOpeningStock({ ...newOpeningStock, branch });
  };

  const handleRemarks = (e) => {
    setNewOpeningStock({ ...newOpeningStock, remarks: e.target.value });
  };

  const getRowItemLabel = (stockItem, index) => {
    if (typeof stockItem.item_name === "string" && stockItem.item_name) {
      return stockItem.item_name;
    }
    if (stockItem.item_name?.name) {
      return stockItem.item_name.name;
    }
    return `${_t(t("Row"))} ${index + 1}`;
  };

  const openingStockItemAxios = async () => {
    const date =
      `${new Date(documentDate).getFullYear()}-` +
      `${new Date(documentDate).getMonth() + 1}-` +
      `${new Date(documentDate).getDate()}`;

    const slugArray = [];
    let hasValidationError = false;

    newRow.forEach((newStockItem, index) => {
      const itemLabel = getRowItemLabel(newStockItem, index);

      if (newStockItem.item_name === "" || newStockItem.unit === "") {
        hasValidationError = true;
        toast.error(
          `${_t(t("Please select item and unit for"))} ${itemLabel}`,
          commonToast
        );
        return;
      }

      const qtyValue = parseFloat(newStockItem.qty);
      if (!newStockItem.qty || Number.isNaN(qtyValue) || qtyValue <= 0) {
        hasValidationError = true;
        toast.error(`${itemLabel} ${_t(t("qty is 0"))}`, commonToast);
        return;
      }

      const rateValue = parseFloat(newStockItem.rate);
      if (
        newStockItem.rate === "" ||
        newStockItem.rate === null ||
        newStockItem.rate === undefined ||
        Number.isNaN(rateValue) ||
        rateValue <= 0
      ) {
        hasValidationError = true;
        toast.error(`${itemLabel} ${_t(t("rate can not be 0"))}`, commonToast);
        return;
      }

      const isExistingRow = isEditMode && newStockItem.isEdit;
      slugArray.push({
        qty: qtyValue,
        price: rateValue,
        item_id: isExistingRow ? newStockItem.item_id : newStockItem.item_name.id,
        item_name: isExistingRow
          ? newStockItem.item_name
          : newStockItem.item_name.name,
        brand_id: newStockItem.brand?.id || "",
        unit_id: isExistingRow ? newStockItem.unit_id : newStockItem.unit.id,
        unit_name: isExistingRow ? newStockItem.unit : newStockItem.unit.name,
        amount: handleGetAmount(index),
        base_unit: newStockItem.base_unit,
        base_qty: calBaseQty(index),
        conversion_qty: parseFloat(newStockItem.conversion_qty),
      });
    });

    if (hasValidationError || newRow.length !== slugArray.length) {
      return;
    }

    setLoading(true);

    const formData = {
      branch_id: newOpeningStock.branch.id,
      document_date: date,
      remarks: newOpeningStock.remarks,
      item: slugArray,
      total_amount: handleAmount(),
    };

    if (isEditMode) {
      formData.opening_stock_id = id;
    }

    const url = isEditMode
      ? `${BASE_URL}/settings/update-opening-stock`
      : `${BASE_URL}/settings/new-opening-stock`;

    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setOpeningStockList(res.data[0]);
      setOpeningStockForSearch(res.data[1]);
      setLoading(false);
      toast.success(
        _t(
          t(
            isEditMode
              ? "Opening stock has been updated"
              : "Opening stock has been added"
          )
        ),
        commonToast
      );
      history.push(LIST_PATH);
    } catch (err) {
      console.log("check error==>", err);
      setLoading(false);
      if (err.response?.data?.status === 0) {
        toast.error(err.response.data.message, commonToast);
        return;
      }
      toast.error(`${_t(t("Please try again later"))}`, commonToast);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      newOpeningStock.branch !== null &&
      newOpeningStock.document_no !== null &&
      documentDate !== null
    ) {
      if (newRow.length > 0) {
        openingStockItemAxios();
      } else {
        toast.error(`${_t(t("Please add stocks"))}`, commonToast);
      }
    } else {
      toast.error(`${_t(t("Please fill all the required fields"))}`, commonToast);
    }
  };

  return (
    <>
      <Helmet>
        <title>{_t(t(pageTitle))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {loading === true ? (
                      tableLoading()
                    ) : (
                      <div key="smtp-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {_t(t(pageTitle))}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              <div className="form-group col-4 mt-3">
                                <div className="mb-2">
                                  <label htmlFor="document no" className="control-label">
                                    {_t(t("Document no"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    readOnly
                                    type="text"
                                    className="form-control table-text"
                                    id="document_no"
                                    name="document_no"
                                    value={newOpeningStock.document_no || ""}
                                    placeholder="Auto no"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="form-group col-4 text-right mt-3">
                                <div className="mb-2 text-left">
                                  <label
                                    htmlFor="transfer_date"
                                    className="control-label"
                                  >
                                    {_t(t("Document Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    selected={documentDate}
                                    onChange={(date) => setDocumentDate(date)}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Document date"))}
                                    shouldCloseOnSelect={false}
                                    required={!isEditMode}
                                  />
                                </div>
                              </div>

                              <div className="form-group mt-3 col-4">
                                <div className="mb-2">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={branchForSearch || []}
                                  components={makeAnimated()}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={newOpeningStock.branch || null}
                                  classNamePrefix="select"
                                  onChange={handleSetBranch}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={`${_t(t("Please select a branch"))}..`}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="form-group mt-2 col-12">
                                <div className="mb-2">
                                  <label htmlFor="remarks" className="control-label">
                                    {_t(t("Remarks"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <textarea
                                    type="text"
                                    className="form-control table-text"
                                    id="remarks"
                                    name="remarks"
                                    onChange={handleRemarks}
                                    value={newOpeningStock.remarks || ""}
                                    placeholder="Remarks"
                                    style={{ height: "33px" }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div
                              className="table-responsive mt-3 pr-1"
                              style={{ maxHeight: "23rem", overflow: "auto" }}
                            >
                              <table className="table table-bordered-sm table-hover">
                                <thead className="align-middle">
                                  <tr>
                                    {newRow.length === 0 ? (
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span className="t-mr-10" onClick={addNewRow}>
                                          <i
                                            className="fa fa-plus-circle cursor-pointer btn-add-icon"
                                            style={{ fontSize: "22px" }}
                                            aria-hidden="true"
                                          ></i>
                                        </span>
                                      </th>
                                    ) : (
                                      <th style={{ borderBottomColor: "lightgray" }}></th>
                                    )}
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border table-Child"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Item Name"))}
                                      </span>
                                    </th>
                                    {
                                      enableStockBrand && (
                                        <th
                                          scope="col"
                                          className="table-text text-capitalize align-middle text-center border-1 border"
                                        >
                                          <span style={{ fontWeight: 500 }}>
                                            {_t(t("Brand"))}
                                          </span>
                                        </th>
                                      )
                                    }
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Unit"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Rate"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Amount"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Base unit"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Base qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                      style={{ minWidth: "9rem" }}
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Conversion unit"))}
                                      </span>
                                    </th>
                                    {newRow.length > 0 && (
                                      <th
                                        style={{
                                          borderBottomColor: "lightgray",
                                          minWidth: "60px",
                                        }}
                                      ></th>
                                    )}
                                  </tr>
                                </thead>
                                {newRow?.length > 0 ? (
                                  <tbody className="align-middle">
                                    {newRow.map((stockItem, index) => {
                                      const isExistingRow =
                                        isEditMode && stockItem.isEdit;

                                      return (
                                        <tr className="align-middle" key={stockItem.id}>
                                          <td
                                            className="table-text text-capitalize align-middle text-center table-firstChild"
                                            style={{ minWidth: "60px" }}
                                          >
                                            {index === newRow.length - 1 && (
                                              <span className="t-mr-10" onClick={addNewRow}>
                                                <i
                                                  className="fa fa-plus-circle btn-add-icon"
                                                  style={{ fontSize: "22px" }}
                                                  aria-hidden="true"
                                                ></i>
                                              </span>
                                            )}
                                            &nbsp;
                                            <i
                                              className="fa fa-minus-circle cursor-pointer text-danger"
                                              style={{ fontSize: "22px" }}
                                              onClick={() => delRow(stockItem.id)}
                                            ></i>
                                          </td>
                                          <td className="table-text text-capitalize align-middle text-center">
                                            {isExistingRow ? (
                                              <input
                                                readOnly
                                                type="text"
                                                className="form-control text-center table-Input table-text"
                                                value={stockItem.item_name || ""}
                                              />
                                            ) : (
                                              <Select
                                                options={
                                                  ingredientItemForSearch || []
                                                }
                                                components={makeAnimated()}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                classNamePrefix="select"
                                                onChange={(e) => handleSetItems(index, e)}
                                                maxMenuHeight="12.8rem"
                                                value={stockItem.item_name}
                                                styles={customStyle}
                                                isMenuOpen={true}
                                                menuPosition="fixed"
                                                className="bank-table-input"
                                                placeholder={`${_t(
                                                  t("Please select items")
                                                )}..`}
                                              />
                                            )}
                                          </td>
                                          {
                                            enableStockBrand && (
                                              <td className="table-text text-capitalize align-middle text-center">
                                                <Select
                                                  options={activeBrand || []}
                                                  components={makeAnimated()}
                                                  getOptionLabel={(option) => option.name}
                                                  getOptionValue={(option) => option.id}
                                                  classNamePrefix="select"
                                                  onChange={(e) => handleSetBrand(index, e)}
                                                  maxMenuHeight="12.8rem"
                                                  value={stockItem.brand}
                                                  styles={customStyle}
                                                  menuPlacement={index >= 1 ? "top" : "bottom"}
                                                  isMenuOpen={true}
                                                  menuPosition="fixed"
                                                  className="bank-table-input"
                                                  placeholder={`${_t(
                                                    t("Select brand")
                                                  )}..`}
                                                />
                                              </td>
                                            )
                                          }
                                          <td className="table-text text-capitalize align-middle text-center">
                                            <input
                                              type="number"
                                              step={qtyAmountPoints()}
                                              min="0"
                                              onKeyDown={(evt) =>
                                                ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                                  evt.key
                                                ) && evt.preventDefault()
                                              }
                                              onWheel={(e) => e.target.blur()}
                                              className="form-control text-center table-Input table-text"
                                              id={stockItem.id}
                                              name={stockItem.id}
                                              onChange={(e) => handleItemQty(index, e)}
                                              value={stockItem.qty || ""}
                                              placeholder="Qty"
                                            />
                                          </td>
                                          <td
                                            className="table-text text-capitalize align-middle text-center"
                                            style={
                                              isExistingRow ? undefined : { minWidth: "200px" }
                                            }
                                          >
                                            {isExistingRow ? (
                                              <input
                                                readOnly
                                                type="text"
                                                className="form-control text-center table-Input table-text"
                                                value={stockItem.unit || ""}
                                              />
                                            ) : (
                                              <Select
                                                menuPosition="fixed"
                                                options={
                                                  stockItem.item_units
                                                    ? stockItem.item_units
                                                    : []
                                                }
                                                components={makeAnimated()}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                value={stockItem.unit}
                                                classNamePrefix="select"
                                                onChange={(e) => handleSetUnit(index, e)}
                                                className="bank-table-input"
                                                maxMenuHeight="200px"
                                                styles={customStyle}
                                                placeholder={`${_t(t("please select unit"))}..`}
                                                isDisabled={!stockItem.item_units}
                                              />
                                            )}
                                          </td>
                                          <td className="table-text text-capitalize align-middle text-center">
                                            <input
                                              type="number"
                                              step={qtyAmountPoints()}
                                              min="0"
                                              onKeyDown={(evt) =>
                                                ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                                  evt.key
                                                ) && evt.preventDefault()
                                              }
                                              onWheel={(e) => e.target.blur()}
                                              className="form-control text-center table-Input table-text"
                                              onChange={(e) => handleItemRate(index, e)}
                                              value={stockItem.rate || ""}
                                              placeholder="Price"
                                            />
                                          </td>
                                          <td className="table-text align-middle text-center">
                                            {formatPrice(handleGetAmount(index))}
                                          </td>
                                          <td className="table-text align-middle text-center">
                                            {stockItem.base_unit}
                                          </td>
                                          <td className="table-text align-middle text-center">
                                            {formatPrice(calBaseQty(index))}
                                          </td>
                                          <td className="table-text align-middle text-center">
                                            {stockItem.conversion_qty}
                                          </td>
                                          <td
                                            className="table-text text-capitalize align-middle text-center"
                                            style={{ minWidth: "60px" }}
                                          >
                                            <i
                                              className="fa fa-minus-circle cursor-pointer text-danger"
                                              style={{ fontSize: "22px" }}
                                              onClick={() => delRow(stockItem.id)}
                                            ></i>
                                            &nbsp;
                                            {index === newRow.length - 1 && (
                                              <span className="t-mr-10" onClick={addNewRow}>
                                                <i
                                                  className="fa fa-plus-circle cursor-pointer btn-add-icon"
                                                  style={{ fontSize: "22px" }}
                                                  aria-hidden="true"
                                                ></i>
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                ) : null}
                                {newRow?.length > 0 ? (
                                  <tfoot className="align-middle">
                                    <tr>
                                      <th
                                        scope="col"
                                        colSpan="1"
                                        className="sm-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                       <th
                                         scope="col"
                                         colSpan={enableStockBrand ? 4 : 3}
                                         className="sm-text text-capitalize align-middle text-center border-1 border"
                                       ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{ fontWeight: "bold" }}>
                                          {_t(t("Total"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{ fontWeight: "bold" }}>
                                          {formatPrice(handleAmount())}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                    </tr>
                                  </tfoot>
                                ) : null}
                              </table>
                            </div>

                            <div className="form-group mt-4 pb-2">
                              <div className="d-flex justify-content-end align-items-center">
                                <div className="col-3 d-flex justify-content-end">
                                  <button
                                    type="button"
                                    className="btn rounded hov-btn mr-3"
                                    onClick={() => history.push(LIST_PATH)}
                                  >
                                    <i className="fa fa-arrow-left" aria-hidden="true"></i>{" "}
                                    {_t(t("Back"))}
                                  </button>
                                  <button
                                    disabled={loading}
                                    type="submit"
                                    className="btn btn-primary rounded"
                                  >
                                    <i className="fa fa-floppy-o" aria-hidden="true"></i>{" "}
                                    {_t(t("Save"))}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default OpeningStockAction;
